import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { T } from '../../constants/mockData';
import { ticketsAPI } from '../../services/api';

const STATUSES = [
  { label: 'Open',                    bg: '#EBF0FF', color: '#3B5BDB' },
  { label: 'Pending Action',          bg: '#FFFBEB', color: '#B45309' },
  { label: 'Pending Approval',        bg: '#FFF7ED', color: '#C2410C' },
  { label: 'Pending User Acceptance', bg: '#F5F3FF', color: '#6D28D9' },
  { label: 'Completed',               bg: '#ECFDF5', color: '#059669' },
  { label: 'Rejected',                bg: '#FFF1F2', color: '#BE123C' },
  { label: 'Voided',                  bg: '#F8FAFC', color: '#64748B' },
];

const DROPDOWN_W = 220;

export default function InlineStatusDropdown({ ticketId, value, onChange }) {
  const [open, setOpen]               = useState(false);
  const [saving, setSaving]           = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  const current = STATUSES.find(s => s.label === value) || { label: value || '—', bg: '#F8FAFC', color: '#64748B' };

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = useCallback(() => {
    if (open) { setOpen(false); return; }
    if (!btnRef.current) { setOpen(true); return; }
    const rect    = btnRef.current.getBoundingClientRect();
    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top  = spaceBelow >= 260
      ? rect.bottom + scrollY + 4
      : rect.top + scrollY - 270;
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight >= DROPDOWN_W + 8
      ? rect.left + scrollX
      : rect.right + scrollX - DROPDOWN_W;
    setPortalStyle({ position: 'absolute', top, left, width: DROPDOWN_W, zIndex: 9999 });
    setOpen(true);
  }, [open]);

  const handleSelect = useCallback(async (newStatus) => {
    setOpen(false);
    if (newStatus === value) return;
    setSaving(true);
    try {
      await ticketsAPI.update(ticketId, { status: newStatus });
      onChange(newStatus);
    } catch (err) {
      console.error('InlineStatusDropdown save error:', err);
    } finally {
      setSaving(false);
    }
  }, [ticketId, value, onChange]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        disabled={saving}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: current.bg, color: current.color,
          border: `1px solid ${current.color}33`,
          borderRadius: 99, padding: '4px 8px 4px 10px',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          whiteSpace: 'nowrap', lineHeight: 1.3,
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? '…' : current.label}
        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" style={{ flexShrink: 0 }}>
          <path d="M1 1l4 4 4-4" stroke={current.color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={{
            ...portalStyle,
            background: '#fff',
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden',
          }}
        >
          {STATUSES.map(s => {
            const isActive = s.label === value;
            return (
              <button
                key={s.label}
                onClick={() => handleSelect(s.label)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '9px 14px',
                  border: 'none', background: isActive ? s.bg : 'none',
                  cursor: 'pointer', fontSize: 12, textAlign: 'left',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.g50; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none'; }}
              >
                <span style={{
                  display: 'inline-block', width: 9, height: 9,
                  borderRadius: '50%', background: s.color, flexShrink: 0,
                }} />
                <span style={{ fontWeight: isActive ? 700 : 500, color: isActive ? s.color : '#2D3748' }}>
                  {s.label}
                </span>
                {isActive && (
                  <svg style={{ marginLeft: 'auto' }} width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <polyline points="2,6 5,9 10,3" stroke={s.color} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

